import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import Tabs from '@cloudscape-design/components/tabs'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'

export interface RouteTabItem {
  label: string
  href: string
}

export interface RouteTabsProps {
  tabs: RouteTabItem[]
  children: React.ReactNode
}

function useIsNarrow(): boolean {
  const query = '(max-width: 767px)'
  const [isNarrow, setIsNarrow] = useState<boolean>(
    () => window.matchMedia(query).matches
  )

  useEffect(() => {
    const mql = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isNarrow
}

export default function RouteTabs({ tabs, children }: RouteTabsProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isNarrow = useIsNarrow()

  const activeTab =
    tabs.find(t => pathname === t.href || pathname.startsWith(t.href + '/')) ??
    tabs[0]
  const activeHref = activeTab?.href ?? ''

  if (isNarrow) {
    return (
      <SpaceBetween size="m">
        <Select
          options={tabs.map(t => ({ label: t.label, value: t.href }))}
          selectedOption={{ label: activeTab?.label ?? '', value: activeHref }}
          onChange={({ detail }) =>
            navigate(String(detail.selectedOption.value))
          }
        />
        {children}
      </SpaceBetween>
    )
  }

  return (
    <>
      <Tabs
        tabs={tabs.map(t => ({ id: t.href, label: t.label, content: <></> }))}
        activeTabId={activeHref}
        onChange={({ detail }) => navigate(detail.activeTabId)}
      />
      {children}
    </>
  )
}
